import { defineComponent, Teleport, TransitionGroup } from 'vue';
import { QBanner, QBtn } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import EventBus from '../../event-bus.js';
import StackVertical from '../VerticalStack.jsx';
import './DisplayNotificationHeader.css';

/** Quasar `bg-*` tokens allowed for notification banners */
const ALLOWED_COLORS = new Set([
    'primary', 'secondary', 'accent', 'positive', 'negative', 'info', 'warning', 'dark',
    'red', 'pink', 'purple', 'deep-purple', 'indigo', 'blue', 'light-blue', 'cyan',
    'teal', 'green', 'light-green', 'lime', 'yellow', 'amber', 'orange', 'deep-orange',
    'brown', 'blue-grey', 'grey',
    'grey-1', 'grey-2', 'grey-3', 'grey-4', 'grey-5', 'grey-6', 'grey-7', 'grey-8', 'grey-9', 'grey-10',
]);

const COLORS_PREFER_TEXT_DARK = new Set([
    'warning', 'yellow', 'lime', 'amber', 'light-green',
    'grey-1', 'grey-2', 'grey-3', 'grey-4', 'grey-5',
]);

function sanitizeColor(raw) {
    const s = String(raw == null ? 'negative' : raw).toLowerCase().trim();
    return ALLOWED_COLORS.has(s) ? s : 'negative';
}

function textClassForBannerColor(color) {
    if (COLORS_PREFER_TEXT_DARK.has(color))
        return 'text-dark';
    return 'text-white';
}

function sanitizeWidth(raw) {
    const s = String(raw == null ? 'full' : raw).trim().toLowerCase();
    if (s === 'content')
        return 'content';
    return 'full';
}

function resolveActionField(value, context) {
    return utils.evaluate(value, context);
}

export default defineComponent({
    name: 'DisplayNotificationHeader',
    components: {
        StackVertical,
        QBanner,
        QBtn,
    },
    data: () => ({
        _uid: utils._uid_counter++,
        content: null,
        isvisible: false,
        groups: {},
    }),
    props: {
    },
    created() {
        EventBus.$on('Action-DisplayNotificationHeader', this.performDisplayNotificationHeader);
        EventBus.$on('Action-RemoveNotificationHeader', this.performRemoveNotificationHeader);
    },
    unmounted() {
        EventBus.$off('Action-DisplayNotificationHeader', this.performDisplayNotificationHeader);
        EventBus.$off('Action-RemoveNotificationHeader', this.performRemoveNotificationHeader);
    },
    mounted() {
    },
    computed: {
        utils: function () {
            return utils;
        },
        Base: function () {
            return this;
        },
        GlobalVars: function () {
            return utils.global_variables;
        },
        ServiceVars: function () {
            return utils.service_variables;
        },
        System: function () {
            return {
                LocalStorage: function (key, value) {
                    return '';
                }
            };
        },
        Name: function () {
            return this.controlData ? (this.controlData.Name || 'unnamed') : 'no control data';
        },
        Root: function () {
            return this.root ? this.root : this;
        },
        Parent: function () {
            let p = this.$parent;

            while (p && !p.root) {
                if (p.$attrs && 'c_context' in p.$attrs)
                    p = p.$attrs.c_context;
                else
                    p = p.$parent;
            }

            return p;
        },
        Vars: function () {
            return this.vars;
        },
    },
    methods: {
        dismissGroup(name) {
            if (name in this.groups)
                delete this.groups[name];
            this.isvisible = Object.keys(this.groups).length > 0;
        },
        async performDisplayNotificationHeader(action) {
            utils.log(`DisplayNotificationHeader action handler`);

            if (action.ActionData && action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            try {
                const c = action.ActionData;
                if (!c.ControlData || !c.ControlData.Controls)
                    return

                const name = (action.groupexpn ? utils.evaluate(action.groupexpn, action.context) : '') || '__unnamed__';

                if (c.ControlData.Controls.length === 0) {
                    if (name in this.groups)
                        delete this.groups[name];

                    this.isvisible = Object.keys(this.groups).length > 0;
                }
                else {
                    const colorRaw = resolveActionField(c.Color, action.context);
                    const widthRaw = resolveActionField(c.Width, action.context);
                    const dismissRaw = resolveActionField(c.Dismissible, action.context);

                    const safeColor = sanitizeColor(colorRaw);
                    const widthMode = sanitizeWidth(widthRaw);
                    const dismissible = Boolean(dismissRaw);
                    const textClass = textClassForBannerColor(safeColor);

                    const id = utils.generateUUID();
                    const stack = (
                        <StackVertical
                            class={[textClass, 'text-weight-medium'].join(' ')}
                            key={id}
                            type="VerticalStack"
                            c_context={action.context}
                            name={c.ControlData ? c.ControlData.Name : ''}
                            root={action.context.root}
                            parentType=""
                            controlData={c.ControlData}
                            controlURL={c.ControlURL}>
                        </StackVertical>
                    );
                    this.groups[name] = {
                        stack,
                        color: safeColor,
                        dismissible,
                        width: widthMode,
                    };
                    this.isvisible = true;
                }

                try {
                    await utils.success(action);
                } catch (e) { }
            }
            catch (e) {
                try {
                    await utils.failure(action, { Data: e });
                } catch (e) { }
            }
            finally {
                try {
                    await utils.complete(action);
                }
                catch (e) { }

                action.FinishFunc(true);
            }
        },
        async performRemoveNotificationHeader(action) {
            utils.log(`RemoveNotificationHeader action handler`);

            if (action.ActionData && action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            try {
                const name = (action.groupexpn ? utils.evaluate(action.groupexpn, action.context) : '') || '__unnamed__';

                if (name in this.groups)
                    delete this.groups[name];

                this.isvisible = Object.keys(this.groups).length > 0;

                try {
                    await utils.success(action);
                } catch (e) { }
            }
            catch (e) {
                try {
                    await utils.failure(action, { Data: e });
                } catch (e) { }
            }
            finally {
                try {
                    await utils.complete(action);
                }
                catch (e) { }

                action.FinishFunc(true);
            }
        },
    },
    render() {
        const keys = Object.keys(this.groups);
        //return (
        //    <v-snackbar value={this.isvisible} timeout={-1} multi-line top light color="error">
        //        {this.content}
        //    </v-snackbar>
        //);
        return (
            <Teleport to="body">
                <div
                    class="z-notify c-DisplayNotificationHeader__dock"
                    role="region"
                    aria-label="App notifications"
                    aria-live="polite"
                >
                    <TransitionGroup
                        name="notification-header"
                        tag="div"
                        class="c-DisplayNotificationHeader__stack"
                    >
                        {keys.map(k => {
                            const entry = this.groups[k];
                            const bgClass = `bg-${entry.color}`;
                            const textClass = textClassForBannerColor(entry.color);
                            const itemClass = [
                                'c-DisplayNotificationHeader__item',
                                entry.width === 'content'
                                    ? 'c-DisplayNotificationHeader__item--content'
                                    : 'c-DisplayNotificationHeader__item--full',
                            ].join(' ');
                            const bannerClass = [
                                'rounded',
                                'dense',
                                'shadow-10',
                                'text-center',
                                bgClass, 
                                textClass,
                                entry.width === 'content' ? 'c-DisplayNotificationHeader__banner--content' : 'c-DisplayNotificationHeader__banner--full',
                            ].join(' ');

                            if (entry.dismissible) {
                                return (
                                    <div key={k} class={itemClass}>
                                        <QBanner
                                            rounded
                                            dense
                                            class={bannerClass}
                                            inlineActions
                                            vSlots={{
                                                default: () => entry.stack,
                                                action: () => (
                                                    <QBtn
                                                        flat
                                                        round
                                                        dense
                                                        icon="mdi-close"
                                                        aria-label="Dismiss"
                                                        onClick={() => this.dismissGroup(k)}
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={k} class={itemClass}>
                                    <QBanner
                                        rounded
                                        dense
                                        class={bannerClass}
                                    >
                                        {entry.stack}
                                    </QBanner>
                                </div>
                            );
                        })}
                    </TransitionGroup>
                </div>
            </Teleport>
        );
    }
});
